import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Globals } from '../../global/globals';
import { AnnotationRepository } from '../ports/annotation-repository.port';
import { AnnotationRow } from '../domain/types';

/** Shape returned by the backend GET/PUT endpoint. */
interface AnnotationDto {
  id: string;
  claimRef: string;
  type: string;
  text: string | null;
  note: string | null;
  ocrText: string | null;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  sessionId: string;
  createdAt: string;
}

/** Shape sent to the backend PUT endpoint. */
interface AnnotationCreateDto {
  clientId: string;
  claimRef: string;
  type: string;
  text?: string;
  note?: string;
  ocrText?: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  sessionId: string;
}

function dtoToRow(dto: AnnotationDto, documentId: string): AnnotationRow {
  return {
    id: dto.id,
    document_id: documentId,
    claim_id: dto.claimRef,
    type: dto.type as AnnotationRow['type'],
    text: dto.text != null && dto.text !== '' ? dto.text : undefined,
    note: dto.note != null && dto.note !== '' ? dto.note : undefined,
    ocr_text: dto.ocrText != null && dto.ocrText !== '' ? dto.ocrText : undefined,
    page: dto.page,
    x: dto.x,
    y: dto.y,
    width: dto.width,
    height: dto.height,
    color: dto.color,
    session_id: dto.sessionId,
    created_at: dto.createdAt,
  };
}

function rowToCreateDto(row: AnnotationRow): AnnotationCreateDto {
  const dto: AnnotationCreateDto = {
    clientId: row.id,
    claimRef: row.claim_id,
    type: row.type,
    page: row.page,
    x: row.x,
    y: row.y,
    width: row.width,
    height: row.height,
    color: row.color,
    sessionId: row.session_id,
  };
  if (row.text != null && row.text !== '') dto.text = row.text;
  if (row.note != null && row.note !== '') dto.note = row.note;
  if (row.ocr_text != null && row.ocr_text !== '') dto.ocrText = row.ocr_text;
  return dto;
}

/**
 * HTTP adapter for AnnotationRepository. Stores annotations per
 * (project, paper, authenticated user) on the backend.
 * projectId is read from the current route snapshot; paperId is extracted
 * from the documentId by stripping the `paper:` prefix.
 */
@Injectable()
export class HttpAnnotationsAdapter implements AnnotationRepository {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly route = inject(ActivatedRoute);

  private baseUrl(documentId: string): string {
    // projectId may live on the parent route (the annotator/data-extraction pages are children of
    // `projects/:projectId`, whose param is not inherited into the child snapshot by default).
    const projectId =
      this.route.snapshot.paramMap.get('projectId') ??
      this.route.parent?.snapshot.paramMap.get('projectId') ??
      '';
    const paperId = documentId.replace(/^paper:/, '');
    return `${this.globals.backendUri}/projects/${projectId}/papers/${paperId}/annotations`;
  }

  async loadFor(documentId: string): Promise<AnnotationRow[]> {
    const dtos = await firstValueFrom(this.http.get<AnnotationDto[]>(this.baseUrl(documentId)));
    return dtos.map((d) => dtoToRow(d, documentId));
  }

  async submitAll(documentId: string, annotations: readonly AnnotationRow[]): Promise<void> {
    const body: AnnotationCreateDto[] = annotations.map(rowToCreateDto);
    const dtos = await firstValueFrom(
      this.http.put<AnnotationDto[]>(this.baseUrl(documentId), body),
    );
    // Discard returned dtos; caller only needs success/failure semantics.
    void dtos;
  }

  async delete(documentId: string): Promise<void> {
    await firstValueFrom(this.http.delete<void>(this.baseUrl(documentId)));
  }

  async runOcr(documentId: string, annotation: AnnotationRow): Promise<AnnotationRow> {
    const body = {
      page: annotation.page,
      x: annotation.x,
      y: annotation.y,
      width: annotation.width,
      height: annotation.height,
      claimRef: annotation.claim_id,
      color: annotation.color,
      sessionId: annotation.session_id,
    };
    const dto = await firstValueFrom(
      this.http.post<AnnotationDto>(`${this.baseUrl(documentId)}/${annotation.id}/ocr`, body),
    );
    return dtoToRow(dto, documentId);
  }
}
